import React from 'react';
import { Link } from 'react-router-dom';
import RichText from './RichText';
import ReportShell, { useReportContext, useScrollToHash } from './ReportShell';
import { answersByFollowUp, useSittingReport } from '../sittingReport';
import { openingAddsNothing } from '../promptText';
import { MARKS, MARK_ORDER, markLabel } from '../marks';

/**
 * Feedback: the student's own answers, each with its mark and what that
 * mark means on that follow-up, a key to the marks, and a report written
 * across the whole paper or interview. The model answers are on Review,
 * linked question by question.
 *
 * The marks and their descriptions are authored content, read back as
 * written. The report is the one generated part, and it only shows when it
 * actually came back: narrative.py raises rather than invent one, and the
 * rest of the page is real and complete without it.
 */
export default function InterviewFeedbackPage() {
  const ctx = useReportContext();
  const { status, sitting, narrative, reportStatus } = useSittingReport(
    ctx.runIds, { withReport: true, mode: ctx.mode },
  );
  useScrollToHash(status === 'ready');
  const written = ctx.mode === 'written';

  const questions = sitting.map((q) => ({ ...q, followUps: answersByFollowUp(q) }));
  const counts = { strong: 0, adequate: 0, weak: 0 };
  questions.forEach((q) => q.followUps.forEach((f) => {
    if (f.band in counts) counts[f.band] += 1;
  }));
  const marked = counts.strong + counts.adequate + counts.weak;

  return (
    <ReportShell active="feedback" title="Feedback" status={status}>
      <section className="ir-report" aria-labelledby="ir-report-title">
        <h2 id="ir-report-title">Your report</h2>
        {reportStatus === 'loading' && (
          <p className="ib__state" role="status">
            Your report takes about 15 seconds. Your marks are below.
          </p>
        )}
        {reportStatus === 'ready' && narrative && <RichText text={narrative} />}
        {reportStatus === 'unavailable' && (
          <p className="ir-mark-why">
            The report is not available right now. Your marks are below.
          </p>
        )}
      </section>

      <p className="ir-summary">
        {marked
          ? `${marked} answer${marked === 1 ? '' : 's'} marked: ${MARK_ORDER.map((k) => `${counts[k]} ${MARKS[k].label}`).join(', ')}`
          : 'No answers were marked.'}
      </p>

      <div className="ir-label">What the marks mean</div>
      <div className="ir-key">
        {MARK_ORDER.map((k) => (
          <div className="ir-key-row" key={k}>
            <span className={`ir-mark ir-mark--${k}`}>{MARKS[k].label}</span>
            <span>{MARKS[k].description}</span>
          </div>
        ))}
      </div>

      {questions.map((q, i) => (
        <section className="ir-q" id={`q-${i + 1}`} key={q.run_id}>
          <h2 className="ir-q-title">Question {i + 1}</h2>
          {!openingAddsNothing(q.opening_prompt, q.followUps.find((f) => !f.opening_only)?.prompt) && (
            <RichText text={q.opening_prompt} />
          )}

          {q.followUps.map((f) => (
            <div className="ir-part" key={f.rung_id}>
              {!f.opening_only && <RichText text={f.prompt} className="ir-part-prompt" />}
              {f.answers.length > 0 ? (
                f.replies.map((r, j) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={j}>
                    {r.asked && (
                      <div className="ir-asked">
                        <span className="ir-answer-label">Examiner followed up</span>
                        <RichText text={r.asked} />
                      </div>
                    )}
                    <div className="ir-answer">{r.text}</div>
                  </div>
                ))
              ) : (
                <div className="ir-answer ir-answer--empty">
                  {f.band ? 'No answer given.' : (written ? 'You did not reach this part.' : 'The interview did not reach this follow-up.')}
                </div>
              )}
              <div className="ir-mark-why">
                <span className={`ir-mark ir-mark--${f.band || 'none'}`}>
                  {f.band ? markLabel(f.band) : 'Not marked'}
                </span>
                {/* What this mark looks like on this follow-up, in the marking
                    guide's words. Not shown for a blank answer: the guide
                    describes a wrong answer, not a missing one. */}
                {f.answers.length > 0 && f.rubric_text && <RichText text={f.rubric_text} className="ir-mark-desc" />}
              </div>
            </div>
          ))}

          <Link className="ir-crosslink" to={`${ctx.base}/review#q-${i + 1}`} state={ctx.linkState}>
            Model answer for this question →
          </Link>
        </section>
      ))}
    </ReportShell>
  );
}
